/**
 * Discovers the current OPEN KotH round on-chain so the /watch broadcast view
 * can auto-follow the keeper's rolling rounds instead of freezing on a single
 * `?round=` id.
 *
 * `koth_end_round` flips a round to Settled (status 1) and the keeper opens a
 * fresh round id roughly every minute — so a fixed `?round=` stream dies after
 * one round. This hook scans the program for KothRound accounts still Open
 * (status byte == 0) via a `getProgramAccounts` memcmp filter and returns the
 * latest by round id, re-checking every few seconds.
 *
 * Disabled (returns null, polls nothing) unless `enabled` — only broadcast mode
 * runs this; the normal player lobby keeps its URL/localStorage resolution and
 * must never start hammering `getProgramAccounts`.
 *
 * Scope: the program is shared across launches, so this filters by `season`
 * when one is known locally (best-effort), otherwise returns the globally
 * latest open round — correct for a single-launch deployment like crownclash,
 * but a multi-launch unmanned box should pass the launch's season PDA.
 *
 * Sticky: once following a round, a poll that finds none (the gap between a
 * settle and the next round opening) keeps the last id rather than clearing —
 * so the winner card stays up until the next round actually opens.
 */
import { BN } from "@coral-xyz/anchor";
import { useConnection } from "@solana/wallet-adapter-react";
import { PublicKey } from "@solana/web3.js";
import bs58 from "bs58";
import { useEffect, useMemo, useRef, useState } from "react";

import { buildReadonlyProgram } from "../../explorer/anchor";

const POLL_MS = 8000;

// Byte offset of `KothRound.status` in the account data, including the 8-byte
// Anchor discriminator. Layout (see state.rs::KothRound):
//   disc(8) round_id(8) keeper(32) created_at(8) expires_at(8)
//   initial_expires_at(8) current_king(32) touch_count(4) pot_lamports(8)
//   → status(u8) sits at byte 116. Open == 0, Settled == 1.
const STATUS_OFFSET = 116;
const OPEN_STATUS_B58 = bs58.encode(Uint8Array.from([0]));

interface DecodedRoundRow {
  account: { roundId: BN; season?: PublicKey; status: number };
}

export function useCurrentOpenRound(
  enabled: boolean,
  seasonPda?: PublicKey | null,
): BN | null {
  const { connection } = useConnection();
  const program = useMemo(() => buildReadonlyProgram(connection), [connection]);
  const [openId, setOpenId] = useState<BN | null>(null);
  const openIdRef = useRef<BN | null>(null);

  // Stabilise the season scope across renders so a fresh PublicKey identity
  // doesn't re-arm the poll loop each render.
  const seasonKey = seasonPda ? seasonPda.toBase58() : null;

  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | null = null;

    const ns = (
      program.account as unknown as Record<
        string,
        { all: (filters?: unknown) => Promise<DecodedRoundRow[]> }
      >
    )["kothRound"];
    if (!ns || typeof ns.all !== "function") return;

    const tick = async (): Promise<void> => {
      try {
        const rows = await ns.all([
          { memcmp: { offset: STATUS_OFFSET, bytes: OPEN_STATUS_B58 } },
        ]);
        if (cancelled) return;
        let open = rows.filter((r) => r.account.status === 0);
        if (seasonKey) {
          open = open.filter((r) => r.account.season?.toBase58() === seasonKey);
        }
        // Latest open round = highest round id.
        const latest = open.reduce<BN | null>(
          (max, r) => (max && max.gte(r.account.roundId) ? max : r.account.roundId),
          null,
        );
        // Sticky: only advance to a new non-null id; never clear to null.
        if (latest && latest.toString() !== openIdRef.current?.toString()) {
          openIdRef.current = latest;
          setOpenId(latest);
        }
      } catch {
        // Transient RPC error — keep the last followed id and retry next tick.
      }
      if (!cancelled) timer = setTimeout(tick, POLL_MS);
    };
    void tick();

    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [enabled, program, seasonKey]);

  return openId;
}
